import { useMemo } from 'react'

import { usePersistedURLQueryParam } from '@sketch/utils'
// eslint-disable-next-line no-restricted-imports
import { ShareSortOrder } from '@sketch/gql-types/expansive'

type SortBy = 'name' | 'created-at' | 'last-modified'
type SortOrder = 'asc' | 'desc'
type SetSortBy = (sortBy: SortBy) => void
type SetSortOrder = (order: SortOrder) => void

interface useSearchReturn {
  sort: ShareSortOrder
  sortBy: SortBy
  sortOrder: SortOrder
  setSortBy: SetSortBy
  setSortOrder: SetSortOrder
}

const MAP_SORT_ORDER: Record<SortBy, Record<SortOrder, ShareSortOrder>> = {
  'created-at': {
    asc: 'CREATED_AT_ASC',
    desc: 'CREATED_AT_DESC',
  },
  'last-modified': {
    asc: 'LAST_MODIFIED_ASC',
    desc: 'LAST_MODIFIED_DESC',
  },
  name: {
    asc: 'NAME_ASC',
    desc: 'NAME_DESC',
  },
}

/**
 * Custom hook to get values from URL related to sorting search results.
 */
export const useSortingSettings = (): useSearchReturn => {
  const [sortByRaw, setSortBy] = usePersistedURLQueryParam('sort-by')
  const [sortOrderRaw, setSortOrder] = usePersistedURLQueryParam('sort-order')

  // The default search value is LAST_MODIFIED_DESC
  const sortBy = (sortByRaw as SortBy) || 'last-modified'
  const sortOrder = (sortOrderRaw as SortOrder) || 'desc'

  const memoizedValue = useMemo(
    () => ({
      sort: MAP_SORT_ORDER[sortBy][sortOrder],
      sortBy,
      sortOrder,
      setSortBy,
      setSortOrder,
    }),
    [sortBy, sortOrder, setSortBy, setSortOrder]
  )

  return memoizedValue
}
